import React, { useCallback } from 'react';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { CheckoutFormData } from '../types';

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
);

interface StripeCheckoutFormProps {
  formData: CheckoutFormData;
  totalAmount: number;
  discountAmount: number;
  onError: (message: string) => void;
  turnstileToken?: string;
}

const StripeCheckoutForm: React.FC<StripeCheckoutFormProps> = ({
  formData,
  totalAmount,
  discountAmount,
  onError,
  turnstileToken,
}) => {
  const fetchClientSecret = useCallback(async () => {
    // Filter out incomplete student entries (Team tier allows partial roster)
    const completeStudents = formData.students.filter(
      (s) => s.firstName && s.lastName && s.email
    );

    const res = await fetch('/api/stripe/create-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...formData,
        students: completeStudents,
        totalAmount: Math.round(totalAmount * 100),
        discountAmount: Math.round(discountAmount * 100),
        turnstileToken: turnstileToken || undefined,
      }),
    });

    if (!res.ok) {
      const err = await res.json();
      onError(err.error || 'Failed to initialize payment. Please try again.');
      throw new Error('Failed to create checkout session');
    }

    const data = await res.json();
    return data.clientSecret;
  }, [formData, totalAmount, discountAmount, onError, turnstileToken]);

  return (
    <div className="stripe-checkout-container">
      <EmbeddedCheckoutProvider
        stripe={stripePromise}
        options={{ fetchClientSecret }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
};

export default StripeCheckoutForm;
